import { clsx } from '@payaca/utilities/cvaMerge';
import { useMemo } from 'react';

export type ButtonVariantProps = {
  variant?:
    | 'solid'
    | 'outline'
    | 'dashed'
    | 'ghost'
    | 'soft'
    | 'white'
    | 'link'
    | 'link-inline';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  colour?:
    | 'black'
    | 'grey'
    | 'green'
    | 'blue'
    | 'red'
    | 'yellow'
    | 'white'
    | 'job-brand';
  linkColour?: 'blue' | 'grey' | 'dark' | 'white';
  shape?: 'pill' | 'rounded';
};

const useButtonClassName = ({
  variant = 'solid',
  size = 'md',
  colour = 'blue',
  shape = 'rounded',
  linkColour = 'blue',
}: ButtonVariantProps) => {
  const variantClass = useMemo(() => {
    switch (variant) {
      case 'solid':
        return 'btn--solid';
      case 'outline':
        return 'btn--outline';
      case 'ghost':
        return 'btn--ghost';
      case 'soft':
        return 'btn--soft';
      case 'white':
        return 'btn--white-variant';
      case 'dashed':
        return 'btn--white-variant border-dashed';
      case 'link':
        return 'btn--link';
      case 'link-inline':
        return 'btn--link-inline';
    }
  }, [variant]);

  const sizeClass = useMemo(() => {
    switch (size) {
      case 'xs':
        return 'btn--xs';
      case 'sm':
        return 'btn--sm';
      case 'md':
        return '';
      case 'lg':
        return 'btn--lg';
    }
  }, [size]);

  const colourClass = useMemo(() => {
    switch (colour) {
      case 'black':
        return 'btn--black';
      case 'grey':
        return 'btn--gray';
      case 'green':
        return 'btn--green';
      case 'blue':
        return 'btn--blue';
      case 'red':
        return 'btn--red';
      case 'yellow':
        return 'btn--yellow';
      case 'white':
        return 'btn--white';
      case 'job-brand':
        return 'btn--job-brand';
    }
  }, [colour]);

  const linkColourClass = useMemo(() => {
    switch (linkColour) {
      case 'dark':
        return 'linkbtn--dark';
      case 'grey':
        return 'linkbtn--gray';
      case 'blue':
        return 'linkbtn--blue';
      case 'white':
        return 'linkbtn--white';
    }
  }, [linkColour]);

  if (variant === 'link-inline') {
    return (
      `btn--link-inline ${linkColourClass}` + (sizeClass ? ` ${sizeClass}` : '')
    );
  }

  if (variant === 'link') {
    return `btn ${variantClass} ${linkColourClass} ${sizeClass} ${
      shape === 'pill' ? 'rounded-full' : 'rounded-lg'
    }`;
  }

  return clsx(
    'btn',
    variantClass,
    colourClass,
    sizeClass,
    shape === 'pill' ? 'rounded-full' : 'rounded-lg'
  );
};

export default useButtonClassName;
